/* lib/searchIndex.js — агрегатор данных в плоский индекс для CommandPalette (S13, §2.1).
   buildSearchIndex({ingredients,recipes,menus,chats,skills}) собирает сущности
   (Сырьё/ТТК/Меню), чаты, действия и разделы в единый массив SearchEntry:
   { id, type, group, label, sublabel, icon, href, keywords }.

   Данные — БОЕВЫЕ: CommandPaletteContext при первом открытии палитры грузит реестры
   через lib/api (listIngredients / listTTKBriefs / listMenuBriefs / listSkills) и текущий
   список чатов из ChatProvider, и передаёт их сюда. Сам модуль чист (без window/сети/мока).

   Поиск регистронезависим для кириллицы (явная нормализация .toLowerCase() с обеих сторон).
   Маршруты — каноничные роуты прототипа (см. app/routes.jsx). */

import { isPathAllowed } from './planSections.js';
import { REPORTS_SECTION_ENABLED } from './reportsSection.js';
import { canUseAssistant } from './assistantAccess.js';

const norm = (s) => (s == null ? '' : String(s)).toLowerCase().trim();

// ── Предустановленные действия (создание / быстрые переходы) ──
const PALETTE_ACTIONS = [
  { id: 'action-new-ingredient', type: 'action', group: 'Действия', label: 'Создать ингредиент',   icon: 'tag',     href: '/kitchen?tab=ingredients&edit=card-new' },
  { id: 'action-new-ttk',        type: 'action', group: 'Действия', label: 'Создать ТТК',           icon: 'book',    href: '/kitchen?tab=recipes&edit=ttk-new' },
  { id: 'action-new-semi',       type: 'action', group: 'Действия', label: 'Создать полуфабрикат',  icon: 'box',     href: '/kitchen?tab=semiproducts&edit=ttk-new-semi' },
  { id: 'action-new-menu',       type: 'action', group: 'Действия', label: 'Создать проект меню',   icon: 'layers',  href: '/kitchen?tab=menus&edit=menu-new' },
  { id: 'action-new-chat',       type: 'action', group: 'Действия', label: 'Новый чат',             icon: 'chat',    href: '/chat?new=1' },
];

// ── Разделы (ключевые роуты приложения) ──
const PALETTE_SECTIONS = [
  { id: 'nav-chat',         type: 'section', group: 'Разделы', label: 'Аргус',              icon: 'chat',         href: '/chat' },
  { id: 'nav-ingredients',  type: 'section', group: 'Разделы', label: 'Продукция · Сырьё', icon: 'tag',          href: '/kitchen?tab=ingredients' },
  { id: 'nav-recipes',      type: 'section', group: 'Разделы', label: 'Продукция · ТТК',          icon: 'book',         href: '/kitchen?tab=recipes' },
  { id: 'nav-semiproducts', type: 'section', group: 'Разделы', label: 'Продукция · Полуфабрикаты',icon: 'box',          href: '/kitchen?tab=semiproducts' },
  { id: 'nav-menus',        type: 'section', group: 'Разделы', label: 'Продукция · Проекты меню', icon: 'layers',       href: '/kitchen?tab=menus' },
  { id: 'nav-analytics',    type: 'section', group: 'Разделы', label: 'Аналитика',            icon: 'analytics',    href: '/analytics' },
  ...(REPORTS_SECTION_ENABLED ? [{ id: 'nav-reports',      type: 'section', group: 'Разделы', label: 'Отчёты',               icon: 'reports',      href: '/reports' }] : []),
  { id: 'nav-restaurant',   type: 'section', group: 'Разделы', label: 'Заведение · профиль',  icon: 'restaurant',   href: '/settings?tab=business&biz=restaurant' },
  { id: 'nav-knowledge',    type: 'section', group: 'Разделы', label: 'База знаний',          icon: 'file-text',    href: '/settings?tab=business&biz=knowledge' },
  { id: 'nav-notifications',type: 'section', group: 'Разделы', label: 'Уведомления',          icon: 'notifications',href: '/notifications' },
  { id: 'nav-settings',     type: 'section', group: 'Разделы', label: 'Аккаунт и настройки',  icon: 'settings',     href: '/settings' },
];

// ── Сборка плоского индекса из БОЕВЫХ данных ──
// ingredients: {id,name,type_label} (listIngredients); recipes: {id,kind,title,...}
// (listTTKBriefs — блюда+п/ф); menus: {id,name,status_label} (listMenuBriefs);
// chats: список ChatProvider {id,title,skill_slug,...}; skills: {slug,name} (listSkills).
// workspace — активное пространство: записи, ведущие в раздел, который закрыл тариф
// пространства (workspace.sections; сейчас — «Аналитика»), из индекса выбрасываются.
// Иначе палитра находила бы «Аналитику», а клик упирался в редирект гарда на /chat.
function buildSearchIndex({ ingredients = [], recipes = [], menus = [], chats = [], skills = [], workspace = null } = {}) {
  const out = [];

  // Сущности: Сырьё (card)
  (ingredients || []).forEach((c) => {
    out.push({
      id: 'card-' + c.id, type: 'entity', kind: 'card', group: 'Сущности',
      label: c.name, sublabel: 'Сырьё · ' + (c.type_label || ''), icon: 'tag',
      href: '/kitchen?tab=ingredients&edit=card-' + c.id,
      keywords: norm((c.name || '') + ' ' + (c.type_label || '')),
    });
  });

  // Сущности: ТТК (dish + semiproduct)
  (recipes || []).forEach((r) => {
    const isSemi = r.kind === 'semiproduct';
    out.push({
      id: 'ttk-' + r.id, type: 'entity', kind: 'ttk', group: 'Сущности',
      label: r.title, sublabel: (isSemi ? 'Полуфабрикат' : 'ТТК · Блюдо'), icon: isSemi ? 'box' : 'book',
      href: '/kitchen?tab=' + (isSemi ? 'semiproducts' : 'recipes') + '&edit=ttk-' + r.id,
      keywords: norm((r.title || '') + ' ' + (r.kind_label || '')),
    });
  });

  // Сущности: Проекты меню (menu)
  (menus || []).forEach((m) => {
    out.push({
      id: 'menu-' + m.id, type: 'entity', kind: 'menu', group: 'Сущности',
      label: m.name, sublabel: 'Проект меню · ' + (m.status_label || ''), icon: 'layers',
      href: '/kitchen?tab=menus&edit=menu-' + m.id,
      keywords: norm((m.name || '') + ' ' + (m.status_label || '')),
    });
  });

  // Чаты (реальный список из ChatProvider, включая дочерние/spawned)
  const skillName = (slug) => { const s = (skills || []).find((x) => x.slug === slug); return s ? s.name : null; };
  (chats || []).forEach((c) => {
    const sk = skillName(c.skill_slug);
    out.push({
      id: 'chat-' + c.id, type: 'chat', group: 'Чаты',
      label: c.title, sublabel: sk ? ('Чат · ' + sk) : 'Чат', icon: 'chat',
      href: '/chat/' + c.id,
      keywords: norm((c.title || '') + ' ' + (sk || '') + ' ' + (c.skill_slug || '')),
    });
  });

  // Действия и разделы
  PALETTE_ACTIONS.forEach((a) => out.push({ ...a, keywords: norm(a.label) }));
  PALETTE_SECTIONS.forEach((s) => out.push({ ...s, keywords: norm(s.label) }));

  // №523: участнику без Аргуса — ни раздела, ни чатов, ни «Нового чата»
  const chatOk = canUseAssistant(workspace);
  return out.filter((e) => isPathAllowed(e.href, workspace)
    && (chatOk || !(typeof e.href === 'string' && e.href.startsWith('/chat'))));
}

// Поиск по индексу: непустой запрос → matching по keywords; пустой → null (вызывающий
// показывает recent + actions). Сохраняет порядок индекса (группировка — на стороне UI).
function filterIndex(index, query) {
  const q = norm(query);
  if (!q) return null;
  return (index || []).filter((e) => e.keywords.indexOf(q) >= 0);
}

// Публичный объект сохраняет прежний контракт: .buildSearchIndex (контекст),
// .searchIndex = фильтр (CommandPalette), .PALETTE_ACTIONS (пустое состояние).
const searchIndexApi = {
  buildSearchIndex, searchIndex: filterIndex, PALETTE_ACTIONS, PALETTE_SECTIONS, norm,
};

export { searchIndexApi as searchIndex };
